import React, { useRef, useState } from 'react';

import { Clear } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { MAIN_THEME } from '@styles';

import FormFieldWrapper from './FormFieldWrapper';
import { IFormFieldInputBaseProps } from '.';

import clsx from 'clsx';
import dayjs from 'dayjs';
import DateService from 'src/core/services/DateService';
import { makeStyles } from 'tss-react/mui';

export interface IDatePickerFormFieldProps extends IFormFieldInputBaseProps {
  value: string;
  maxDate?: Date;
  minDate?: Date;
  material?: boolean;
  onDateChange: (date: Date | '') => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  canClearDate?: boolean;
  classes?: {
    labelWrapper?: string;
    inputContainer?: string;
  };
}

const useStyles = makeStyles()(() => ({
  inputProps: {
    fontFamily: MAIN_THEME.font.regular,
    fontSize: `${MAIN_THEME.fontSize.sm} !important`,
    height: 35,
    width: '100%',
    border: `1px solid ${MAIN_THEME.color.white86}`,
    borderRadius: '5px',
    backgroundColor: MAIN_THEME.color.white98,
    '&:disabled': {
      cursor: 'not-allowed'
    },
    '& .MuiInputBase-input': {
      fontSize: MAIN_THEME.fontSize.sm,
      fontFamily: MAIN_THEME.font.regular,
      lineHeight: '15px',
      height: 31,
      width: '100%',
      padding: '0 15px',
      margin: '0 auto',
      backgroundColor: MAIN_THEME.color.white98,
      boxSizing: 'border-box',
      borderRadius: '5px',
      '&::placeholder': {
        fontStyle: 'italic',
        fontFamily: MAIN_THEME.font.regular,
        color: MAIN_THEME.color.white86,
        opacity: 1,
        fontSize: MAIN_THEME.fontSize.sm
      }
    },
    '& .MuiInput-underline': {
      '&:before': {
        borderBottom: 'none'
      },
      '&:after': {
        borderBottom: 'none'
      },
      '&:hover:not(.Mui-disabled):before': {
        borderBottom: 'none'
      }
    },
    '& .MuiButtonBase-root': {
      marginRight: '3px'
    }
  },
  inputError: {
    borderColor: `${MAIN_THEME.color.red} !important`,
    outlineColor: MAIN_THEME.color.red
  },
  disabledInput: {
    '& .Mui-disabled::before': {
      borderBottom: 0
    },
    '& .Mui-disabled': {
      cursor: 'not-allowed'
    }
  },
  materialInput: {
    width: '100%',
    backgroundColor: 'unset',
    border: 'none',
    borderBottom: `1px solid ${MAIN_THEME.color.white86}`,

    '& .MuiFilledInput-root': {
      height: 35,
      backgroundColor: 'unset !important',
      paddingRight: '1.2em',
      borderRadius: '0',
      '::before': {
        borderBottom: 'none !important'
      },
      '::after': {
        borderBottom: 'none !important'
      },
      '& .MuiFilledInput-input': {
        width: '100%',
        backgroundColor: 'unset',
        color: MAIN_THEME.color.black,
        fontFamily: `${MAIN_THEME.font.regular} !important`,
        fontSize: `${MAIN_THEME.fontSize.sm} !important`,
        padding: '4px 0px 0px 4px'
      },
      '& .MuiButtonBase-root': {
        height: '0.6em',
        width: '0.6em',
        marginTop: '7px',
        padding: '0.3em',
        '& .MuiSvgIcon-root': {
          fontSize: `${MAIN_THEME.fontSize.sm} !important`
        }
      }
    }
  },
  clearBtn: {
    cursor: 'pointer',
    height: '12px',
    width: '12px'
  }
}));

const DatePickerFormField: React.FC<IDatePickerFormFieldProps> = ({
  style,
  name,
  title,
  error,
  disabled,
  value,
  maxDate,
  minDate,
  material,
  placeholder,
  inputOnly,
  onDateChange,
  onBlur,
  canClearDate = true,
  classes = {}
}) => {
  const { classes: inputClasses } = useStyles();
  const acceptParse = useRef<boolean>(true);
  const chooseFromCalendar = useRef<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);

  const handleDateChange = (date: Date | null) => {
    if ((date && acceptParse.current) || chooseFromCalendar.current) {
      if (minDate && date) {
        // Use dayjs for timezone-aware comparison
        const selectedDateDayjs = dayjs(date).startOf('day');
        const minDateDayjs = dayjs(minDate).startOf('day');
        if (selectedDateDayjs.isBefore(minDateDayjs)) {
          date = minDate;
        }
      }
      onDateChange(dayjs(date).toDate());
    } else {
      onDateChange('');
    }
  };

  const handleClearDate = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDateChange('');
  };

  const DatePickerComponent = (
    <DatePicker
      open={openDialog}
      onOpen={() => setOpenDialog(true)}
      onClose={() => setOpenDialog(false)}
      value={value ? new Date(value) : null}
      onChange={(newDate) => {
        if (newDate) {
          acceptParse.current = true;
          chooseFromCalendar.current = false;
        } else {
          acceptParse.current = false;
          chooseFromCalendar.current = false;
        }
        handleDateChange(newDate);
      }}
      disabled={disabled}
      minDate={minDate}
      maxDate={maxDate}
      format={DateService.DATE_PICKER_FORMAT}
      orientation="portrait"
      slots={{
        textField: TextField
      }}
      slotProps={{
        textField: (params) => ({
          ...params,
          className: clsx(classes.inputContainer, {
            [inputClasses.inputProps]: !material,
            [inputClasses.materialInput]: material,
            [inputClasses.inputError]: error,
            [inputClasses.disabledInput]: disabled
          }),
          name: name,
          placeholder: placeholder,

          onKeyPressCapture: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              setOpenDialog(true);
            }
          },
          disabled: disabled,
          error: !!error,
          onClick: () => {
            if (!disabled) {
              setOpenDialog(true);
            }
          },
          onBlur: onBlur,
          variant: material ? 'filled' : 'standard',
          InputProps: {
            ...params.InputProps,
            endAdornment: (
              <>
                {canClearDate && !disabled && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear date"
                      onClick={handleClearDate}
                      edge="end"
                      size="small"
                    >
                      <Clear className={inputClasses.clearBtn} />
                    </IconButton>
                  </InputAdornment>
                )}
                {params.InputProps?.endAdornment}
              </>
            )
          }
        })
      }}
    />
  );

  if (inputOnly) {
    return DatePickerComponent;
  }

  return (
    <FormFieldWrapper
      title={title}
      error={error}
      name={name}
      material={material}
      style={style}
    >
      {DatePickerComponent}
    </FormFieldWrapper>
  );
};

export default DatePickerFormField;
